import { AnimatePresence, motion, Variants } from 'framer-motion'
import React, {
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import ReactModal from 'react-modal'
import styled from 'styled-components'
import { ZIndexRange } from '../types/enums'

interface StyledReactModalProps {
  open: boolean
  zIndex: number
}
const StyledReactModal = styled(ReactModal)<StyledReactModalProps>`
  ${props => !props.open && 'pointer-events: none;'}
  z-index: ${props => props.zIndex};
`
interface ShadeProps {
  zIndex: number
  background: string
}
const Shade = styled(motion.div)<ShadeProps>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: ${props => props.zIndex};
  cursor: default;

  background-color: ${props => props.background};
`

function getPosition(placement: ModalPlacement, offset: number) {
  const [vertical, horizontal] = placement.split('-')
  let position = ''

  switch (vertical) {
    case 'top':
      position += `top: ${offset}px;`
      break
    case 'center':
      position += 'top: 50%;'
      break
    case 'bottom':
      position += `bottom: ${offset}px;`
      break
  }

  switch (horizontal) {
    case 'left':
      position += `left: ${offset}px;`
      break
    case 'center':
      position += 'left: 50%;'
      break
    case 'right':
      position += `right: ${offset}px;`
      break
  }

  return position
}
function getTranslation(placement: ModalPlacement): [string, string] {
  const [vertical, horizontal] = placement.split('-')
  let transform: [string, string] = ['0px', '0px']

  if (horizontal === 'center') transform[0] = '-50%'
  if (vertical === 'center') transform[1] = '-50%'

  return transform
}

interface ModalWrapperProps {
  placement: ModalPlacement
  offset: number
  zIndex: number
}
const ModalWrapper = styled(motion.div)<ModalWrapperProps>`
  position: fixed;
  z-index: ${props => props.zIndex + 1};
  cursor: default;

  ${props => getPosition(props.placement, props.offset)}
`

export type ModalPlacement =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'center-left'
  | 'center-center'
  | 'center-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right'
export type ModalDirection = 'from-top' | 'from-bottom'
export interface ModalProps extends PropsWithChildren<object> {
  open: boolean

  /** Id of root element in app */
  appElementId?: string
  closeOnEscape?: boolean
  /** Placement of modal */
  placement?: ModalPlacement
  /** Direction of animation */
  direction?: ModalDirection
  /** Pixel offset depending on placement. Offset is ignored on axes where placement is center */
  offset?: number

  zIndex?: number
  shadeOpacity?: number
  shadeBackground?: string
  /** Disable shade */
  noShade?: boolean

  /**
   * Whether modal should be focused after render'
   * @default true
   * @see https://reactcommunity.org/react-modal/
   */
  shouldFocusAfterRender?: boolean
  /**
   * Whether modal should restore focus to the element that had focus prior to its display
   * @default true
   * @see https://reactcommunity.org/react-modal/
   */
  shouldReturnFocusAfterClose?: boolean

  onClose?: (evt?: React.MouseEvent | React.KeyboardEvent) => void
  /** If left undefined, onClose is fired when shade is clicked */
  onShadeClick?: (event: React.MouseEvent<HTMLDivElement>) => void
  /** Fired when open animation is done */
  onOpenFinished?: () => void
  /** Fired when close animation is done */
  onCloseFinished?: () => void
}

export const Modal: React.FC<ModalProps> = ({
  children,
  open,

  appElementId = 'root',
  closeOnEscape = false,
  placement = 'center-center',
  direction = 'from-bottom',
  offset = 0,

  zIndex = ZIndexRange.Modals,
  shadeOpacity = 0.3,
  shadeBackground = 'black',
  noShade = false,

  shouldFocusAfterRender = true,
  shouldReturnFocusAfterClose = true,

  onClose,
  onShadeClick,

  onOpenFinished,
  onCloseFinished,
}) => {
  if (typeof onShadeClick === 'undefined') onShadeClick = onClose

  const appElementRef = useRef<HTMLElement | null>(
    document.getElementById(appElementId)
  )

  const [isOpen, setIsOpen] = useState(open)

  useEffect(() => {
    open && setIsOpen(true)
  }, [open])

  const shadeVariants = useMemo<Variants>(
    () => ({
      hidden: { opacity: 0 },
      visible: { opacity: shadeOpacity },
    }),
    [shadeOpacity]
  )
  const modalVariants = useMemo<Variants>(() => {
    const [tX, tY] = getTranslation(placement)
    const dir = direction === 'from-bottom' ? 30 : -30

    return {
      hidden: { opacity: 0, x: tX, y: `calc(${tY} + ${dir}px)` },
      visible: { opacity: 1, x: tX, y: tY },
    }
  }, [direction, placement])

  return (
    <StyledReactModal
      appElement={appElementRef.current ?? undefined}
      className="modal"
      overlayClassName="modal-overlay"
      isOpen={isOpen}
      open={open}
      zIndex={zIndex}
      shouldCloseOnEsc={closeOnEscape}
      shouldFocusAfterRender={shouldFocusAfterRender}
      shouldReturnFocusAfterClose={shouldReturnFocusAfterClose}
      onRequestClose={onClose}
    >
      <AnimatePresence>
        {!noShade && open && (
          <Shade
            variants={shadeVariants}
            initial="hidden"
            animate={open ? 'visible' : 'hidden'}
            exit="hidden"
            zIndex={zIndex}
            background={shadeBackground}
            onClick={onShadeClick}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <ModalWrapper
            variants={modalVariants}
            initial="hidden"
            animate={open ? 'visible' : 'hidden'}
            exit="hidden"
            placement={placement}
            offset={offset}
            zIndex={zIndex}
            onAnimationComplete={state =>
              state === 'visible' ? onOpenFinished?.() : onCloseFinished?.()
            }
          >
            {children}
          </ModalWrapper>
        )}
      </AnimatePresence>
    </StyledReactModal>
  )
}
